/**
 * MusicSourcePicker — Imaging Studio Music Beds upgrade (Update #82).
 *
 * Three top-level tabs above the existing category pills:
 *   1. Upload your own       — multi-file dropzone with per-file genre dropdown
 *   2. Royalty-free library  — Internet Archive (live), Jamendo / Freesound /
 *                              Pixabay tiles (configure-key state)
 *   3. AI Generate           — Mubert form + Suno "configure key" tile
 *
 * Selecting a track on any tab calls onTrackAdded(newAssetDoc) so the
 * parent can append to the existing bed list (no refetch needed).
 */
import React, { useEffect, useState } from "react";
import {
  Upload, Loader2, Plus, Sparkles, Wand2, ExternalLink,
  Music as MusicIcon, Library, AlertCircle, Key,
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import AudioPreview from "@/components/AudioPreview";

const BED_GENRES = [
  "R&B", "Urban", "Pop", "Reggae", "Hip-Hop", "News",
  "Sports", "Talk Radio", "Smooth Jazz", "Dance", "Gospel", "Caribbean",
];
const MOODS = ["calm", "energetic", "dramatic", "uplifting", "dark", "smooth", "groovy", "inspirational"];
const DURATION_PRESETS = [30, 60, 90, 120];

const TAB_DEFS = [
  { id: "upload",  label: "Upload your own",     icon: Upload },
  { id: "library", label: "Royalty-free library", icon: Library },
  { id: "ai",      label: "AI generate",         icon: Wand2 },
];


export default function MusicSourcePicker({ onTrackAdded, "data-testid": testid = "music-source-picker" }) {
  const [tab, setTab] = useState("upload");
  const [sources, setSources] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/imaging/library/sources");
        setSources(r.data?.sources || []);
      } catch { setSources([]); }
    })();
  }, []);

  return (
    <div
      className="rounded-xl overflow-hidden mb-5"
      style={{ background: "#0a0d14", border: "0.5px solid #1e2130" }}
      data-testid={testid}
    >
      {/* Tab bar */}
      <div className="flex flex-wrap" style={{ borderBottom: "0.5px solid #1e2130" }}>
        {TAB_DEFS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              data-testid={`music-src-tab-${t.id}`}
              className="inline-flex items-center gap-2 px-4 py-2.5 text-[12px] font-medium transition-colors"
              style={{
                color: active ? "#4a9eff" : "#4a5068",
                borderBottom: active ? "2px solid #4a9eff" : "2px solid transparent",
              }}
            >
              <Icon className="h-3.5 w-3.5" />
              {t.label}
            </button>
          );
        })}
      </div>

      <div className="p-4" style={{ background: "#0f1117" }}>
        {tab === "upload"  && <UploadPanel onTrackAdded={onTrackAdded} />}
        {tab === "library" && <LibraryPanel onTrackAdded={onTrackAdded} sources={sources} />}
        {tab === "ai"      && <AIGeneratePanel onTrackAdded={onTrackAdded} sources={sources} />}
      </div>
    </div>
  );
}


/* ========================================================================== */
/* TAB 1 — UPLOAD YOUR OWN                                                    */
/* ========================================================================== */
function UploadPanel({ onTrackAdded }) {
  const [queue, setQueue] = useState([]);  // {file, genre, uploading, done, error}
  const [hover, setHover] = useState(false);
  const [bulkGenre, setBulkGenre] = useState("");

  const enqueue = (filesLike) => {
    const arr = Array.from(filesLike || []);
    const fresh = arr
      .filter((f) => /\.(mp3|wav|m4a|aac|flac|ogg|aiff)$/i.test(f.name))
      .filter((f) => f.size <= 50 * 1024 * 1024)
      .map((f) => ({ file: f, genre: bulkGenre || "Pop", uploading: false, done: false, error: "" }));
    if (fresh.length < arr.length) {
      toast.warning(`Skipped ${arr.length - fresh.length} unsupported / oversized file(s)`);
    }
    if (fresh.length === 0) return;
    setQueue((q) => [...q, ...fresh]);
  };

  const removeAt = (idx) => setQueue((q) => q.filter((_, i) => i !== idx));
  const setGenreAt = (idx, g) => setQueue((q) => q.map((it, i) => i === idx ? { ...it, genre: g } : it));

  const uploadAll = async () => {
    for (let i = 0; i < queue.length; i++) {
      if (queue[i].done || queue[i].uploading) continue;
      setQueue((q) => q.map((it, j) => j === i ? { ...it, uploading: true, error: "" } : it));
      try {
        const fd = new FormData();
        fd.append("file", queue[i].file);
        fd.append("kind", "bed");
        fd.append("genre", queue[i].genre);
        fd.append("label", queue[i].file.name.replace(/\.[^.]+$/, ""));
        fd.append("source", "user_uploaded");
        const res = await fetch(`${API_BASE}/imaging/assets/upload`, {
          method: "POST",
          body: fd,
          credentials: "include",
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const asset = await res.json();
        onTrackAdded?.(asset);
        setQueue((q) => q.map((it, j) => j === i ? { ...it, uploading: false, done: true } : it));
      } catch (e) {
        setQueue((q) => q.map((it, j) => j === i ? { ...it, uploading: false, error: e?.message || "Upload failed" } : it));
      }
    }
    toast.success("Upload batch complete");
  };

  return (
    <div className="space-y-3" data-testid="upload-panel">
      <label
        onDragOver={(e) => { e.preventDefault(); setHover(true); }}
        onDragLeave={() => setHover(false)}
        onDrop={(e) => { e.preventDefault(); setHover(false); enqueue(e.dataTransfer.files); }}
        className="block rounded-xl text-center cursor-pointer transition-colors"
        style={{
          height: 120,
          background: hover ? "#0d1e35" : "#0a0d14",
          border: `2px dashed ${hover ? "#4a9eff" : "#1e4d7a"}`,
          borderRadius: 10,
          padding: 20,
        }}
        data-testid="upload-dropzone"
      >
        <Upload className="h-6 w-6 mx-auto mb-2" style={{ color: "#4a9eff" }} />
        <div className="text-[13px]" style={{ color: "#a8acbb" }}>Drag MP3, WAV, AIFF, M4A, OGG or FLAC files here</div>
        <div className="text-[11px] mt-0.5" style={{ color: "#4a5068" }}>or click to browse · max 50 MB per file · batch upload supported</div>
        <input
          type="file"
          accept=".mp3,.wav,.m4a,.aac,.flac,.ogg,.aiff,audio/*"
          multiple
          className="hidden"
          onChange={(e) => enqueue(e.target.files)}
          data-testid="upload-file-input"
        />
      </label>

      {queue.length > 0 && (
        <>
          <div className="flex items-center gap-3 flex-wrap" data-testid="upload-queue-controls">
            <div className="text-[11px] uppercase tracking-wider" style={{ color: "#4a5068" }}>
              Bulk genre →
            </div>
            <Select value={bulkGenre} onValueChange={(g) => {
              setBulkGenre(g);
              setQueue((q) => q.map((it) => it.done ? it : { ...it, genre: g }));
            }}>
              <SelectTrigger className="h-8 w-44 bg-bg border-line text-xs" data-testid="bulk-genre-select">
                <SelectValue placeholder="Apply to all" />
              </SelectTrigger>
              <SelectContent>{BED_GENRES.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
            </Select>
            <button
              type="button"
              onClick={uploadAll}
              disabled={queue.every((q) => q.done)}
              data-testid="upload-all-btn"
              className="ml-auto inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md text-[12px] font-semibold transition disabled:opacity-50"
              style={{ background: "#1a3a5c", color: "#4a9eff" }}
            >
              <Upload className="h-3.5 w-3.5" />
              Upload {queue.filter((q) => !q.done).length} file{queue.filter((q) => !q.done).length === 1 ? "" : "s"}
            </button>
          </div>
          <div className="space-y-1.5">
            {queue.map((it, idx) => (
              <div
                key={idx}
                className="flex items-center gap-3 rounded-md px-3 py-2"
                style={{ background: "#0a0d14", border: "0.5px solid #1e2130" }}
                data-testid={`upload-queue-item-${idx}`}
              >
                <MusicIcon className="h-4 w-4 shrink-0" style={{ color: "#4a9eff" }} />
                <div className="min-w-0 flex-1">
                  <div className="text-[12px] truncate" style={{ color: "#c8ccd8" }}>{it.file.name}</div>
                  <div className="text-[10px] font-mono" style={{ color: "#4a5068" }}>
                    {it.file.size >= 1024 * 1024
                      ? `${(it.file.size / 1024 / 1024).toFixed(1)} MB`
                      : `${(it.file.size / 1024).toFixed(0)} KB`}
                    {it.uploading && <span className="ml-2" style={{ color: "#f59e0b" }}>· uploading…</span>}
                    {it.done     && <span className="ml-2" style={{ color: "#10b981" }}>· uploaded</span>}
                    {it.error    && <span className="ml-2" style={{ color: "#ef4444" }}>· {it.error}</span>}
                  </div>
                </div>
                <Select value={it.genre} onValueChange={(g) => setGenreAt(idx, g)} disabled={it.done}>
                  <SelectTrigger className="h-7 w-36 bg-bg border-line text-[11px]" data-testid={`upload-genre-${idx}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>{BED_GENRES.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                </Select>
                {it.uploading
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" style={{ color: "#f59e0b" }} />
                  : !it.done && (
                    <button
                      type="button"
                      onClick={() => removeAt(idx)}
                      className="text-[10px] px-2 py-1 rounded"
                      style={{ color: "#4a5068" }}
                      data-testid={`upload-remove-${idx}`}
                    >Remove</button>
                  )
                }
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}


/* ========================================================================== */
/* TAB 2 — ROYALTY-FREE LIBRARY                                               */
/* ========================================================================== */
function LibraryPanel({ onTrackAdded, sources }) {
  const librarySources = sources.filter((s) => s.kind === "library" || s.kind === "image_library");
  const enabledLib = librarySources.filter((s) => s.enabled);
  const [activeSource, setActiveSource] = useState(enabledLib[0]?.id || "internet_archive");
  useEffect(() => {
    if (enabledLib.length > 0 && !enabledLib.find((s) => s.id === activeSource)) {
      setActiveSource(enabledLib[0].id);
    }
  }, [enabledLib.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const [genre, setGenre] = useState("Smooth Jazz");
  const [q, setQ] = useState("");
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [importingId, setImportingId] = useState("");

  const activeSourceMeta = sources.find((s) => s.id === activeSource);
  const isImageSource = activeSourceMeta?.kind === "image_library";

  const search = async () => {
    setLoading(true); setError(""); setTracks([]);
    try {
      const r = await api.get("/imaging/library/search", { params: { source: activeSource, genre, q, limit: 12 } });
      setTracks(r.data?.tracks || []);
      if ((r.data?.tracks || []).length === 0) setError("No results — try a different genre or keyword");
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Search failed";
      setError(detail);
    } finally { setLoading(false); }
  };

  const addToStation = async (t) => {
    if (isImageSource) {
      toast.message("Pixabay images don't import as audio. Use them for Topic Image in News Center instead.");
      return;
    }
    setImportingId(t.id);
    try {
      const r = await api.post("/imaging/library/import", {
        source: t.source, track_id: String(t.id),
        download_url: t.download_url, name: t.name,
        artist: t.artist, genre, license: t.license,
      });
      onTrackAdded?.(r.data);
      toast.success(`Added "${t.name}" to ${genre} beds`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally { setImportingId(""); }
  };

  return (
    <div className="space-y-3" data-testid="library-panel">
      {/* Source sub-tabs */}
      <div className="flex flex-wrap gap-1.5" data-testid="library-source-tabs">
        {librarySources.map((s) => {
          const active = s.id === activeSource;
          return (
            <button
              key={s.id}
              type="button"
              disabled={!s.enabled}
              onClick={() => s.enabled && setActiveSource(s.id)}
              data-testid={`library-source-${s.id}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium transition disabled:cursor-not-allowed"
              style={
                active
                  ? { background: "#1a3a5c", color: "#4a9eff", border: "0.5px solid #4a9eff66" }
                  : s.enabled
                    ? { background: "#0f1117", color: "#c8ccd8", border: "0.5px solid #1e2130" }
                    : { background: "#0a0d14", color: "#4a5068", border: "0.5px dashed #1e2130" }
              }
            >
              {!s.enabled && <Key className="h-3 w-3" />}
              {s.label}
              {!s.enabled && <span className="text-[9px] font-mono ml-1 opacity-70">configure</span>}
            </button>
          );
        })}
      </div>

      {activeSourceMeta && !activeSourceMeta.enabled && (
        <div
          className="rounded-md px-3 py-2 text-[12px] flex items-start gap-2"
          style={{ background: "#0a0d14", border: "0.5px solid #1e4d7a", color: "#a8acbb" }}
        >
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" style={{ color: "#f59e0b" }} />
          <div>
            <div className="font-semibold" style={{ color: "#f0f2f8" }}>{activeSourceMeta.label} needs a key</div>
            <div className="text-[11px] mt-0.5">{activeSourceMeta.description}</div>
          </div>
        </div>
      )}

      {/* Search controls */}
      <div className="flex flex-wrap items-end gap-2">
        <div>
          <Label className="text-[10px] uppercase tracking-wider" style={{ color: "#4a5068" }}>Genre</Label>
          <Select value={genre} onValueChange={setGenre}>
            <SelectTrigger className="h-8 w-40 bg-bg border-line text-xs mt-1" data-testid="library-genre">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>{BED_GENRES.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <Label className="text-[10px] uppercase tracking-wider" style={{ color: "#4a5068" }}>Keyword (optional)</Label>
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="lofi, jazz, late night…"
            className="h-8 bg-bg border-line text-xs mt-1"
            data-testid="library-keyword"
            onKeyDown={(e) => { if (e.key === "Enter") search(); }}
          />
        </div>
        <button
          type="button"
          onClick={search}
          disabled={loading || !activeSourceMeta?.enabled}
          data-testid="library-search-btn"
          className="inline-flex items-center gap-1.5 px-4 py-1.5 h-8 rounded-md text-[12px] font-semibold transition disabled:opacity-50"
          style={{ background: "#1a3a5c", color: "#4a9eff" }}
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Library className="h-3.5 w-3.5" />}
          Search
        </button>
      </div>

      {error && <div className="text-[11px]" style={{ color: "#ef4444" }} data-testid="library-error">{error}</div>}

      {/* Results grid */}
      {loading && (
        <div className="grid sm:grid-cols-2 gap-2" data-testid="library-skeleton">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-20 rounded-md animate-pulse" style={{ background: "#0a0d14" }} />
          ))}
        </div>
      )}

      {!loading && tracks.length > 0 && (
        <div className="grid sm:grid-cols-2 gap-2" data-testid="library-results">
          {tracks.map((t) => (
            <div
              key={t.id}
              className="rounded-md px-3 py-2.5 space-y-1.5"
              style={{ background: "#0a0d14", border: "0.5px solid #1e2130" }}
              data-testid={`library-track-${t.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-[12px] font-medium truncate" style={{ color: "#f0f2f8" }}>{t.name}</div>
                  <div className="text-[10px] font-mono truncate" style={{ color: "#4a5068" }}>
                    {t.artist ? `${t.artist} · ` : ""}
                    {t.duration_s ? `${Math.floor(t.duration_s / 60)}:${String(t.duration_s % 60).padStart(2, "0")} · ` : ""}
                    {(activeSourceMeta?.label || t.source)}
                  </div>
                </div>
                {t.external_url && (
                  <a
                    href={t.external_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[10px] inline-flex items-center gap-0.5"
                    style={{ color: "#4a5068" }}
                  >
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>

              {isImageSource ? (
                <img src={t.preview_url} alt={t.name} className="w-full h-24 object-cover rounded" loading="lazy" />
              ) : (
                <AudioPreview src={t.preview_url} data-testid={`library-preview-${t.id}`} />
              )}

              <button
                type="button"
                onClick={() => addToStation(t)}
                disabled={importingId === t.id || isImageSource}
                data-testid={`library-add-${t.id}`}
                className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-semibold transition disabled:opacity-50"
                style={isImageSource
                  ? { background: "#0f1117", color: "#4a5068", border: "0.5px solid #1e2130" }
                  : { background: "#1a3a5c", color: "#4a9eff" }
                }
              >
                {importingId === t.id
                  ? <><Loader2 className="h-3 w-3 animate-spin" /> Importing…</>
                  : isImageSource
                    ? "Image (preview only)"
                    : <><Plus className="h-3 w-3" /> Add to station</>
                }
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


/* ========================================================================== */
/* TAB 3 — AI GENERATE                                                        */
/* ========================================================================== */
function AIGeneratePanel({ onTrackAdded, sources }) {
  const aiSources = sources.filter((s) => s.kind === "ai_generate");
  const enabledAi = aiSources.filter((s) => s.enabled);
  const [aiSource, setAiSource] = useState(enabledAi[0]?.id || "mubert");
  useEffect(() => {
    if (enabledAi.length > 0 && !enabledAi.find((s) => s.id === aiSource)) {
      setAiSource(enabledAi[0].id);
    }
  }, [enabledAi.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const [genre, setGenre] = useState("Smooth Jazz");
  const [mood, setMood]   = useState("calm");
  const [duration, setDuration] = useState(60);
  const [description, setDescription] = useState("");
  const [intensity, setIntensity] = useState("medium");
  const [busy, setBusy] = useState(false);

  const sourceMeta = sources.find((s) => s.id === aiSource);

  const generate = async () => {
    if (!sourceMeta?.enabled) return;
    setBusy(true);
    try {
      if (aiSource === "mubert") {
        const r = await api.post("/imaging/generate/mubert", {
          genre, mood, duration, description: description.trim() || null, intensity,
        });
        onTrackAdded?.(r.data);
        toast.success(`Generated ${genre} bed (${duration}s)`);
      } else {
        toast.message("Suno generation lives in BroadcastFlow Music Studio — head over there for full songs.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed", { duration: 7000 });
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="ai-panel">
      <div className="flex flex-wrap gap-1.5" data-testid="ai-source-tabs">
        {aiSources.map((s) => {
          const active = s.id === aiSource;
          return (
            <button
              key={s.id}
              type="button"
              disabled={!s.enabled}
              onClick={() => s.enabled && setAiSource(s.id)}
              data-testid={`ai-source-${s.id}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium transition disabled:cursor-not-allowed"
              style={
                active
                  ? { background: "#1a3a5c", color: "#4a9eff", border: "0.5px solid #4a9eff66" }
                  : s.enabled
                    ? { background: "#0f1117", color: "#c8ccd8", border: "0.5px solid #1e2130" }
                    : { background: "#0a0d14", color: "#4a5068", border: "0.5px dashed #1e2130" }
              }
            >
              {!s.enabled && <Key className="h-3 w-3" />}
              {s.label}
              {!s.enabled && <span className="text-[9px] font-mono ml-1 opacity-70">configure</span>}
            </button>
          );
        })}
      </div>

      {sourceMeta && !sourceMeta.enabled && (
        <div
          className="rounded-md px-3 py-2 text-[12px] flex items-start gap-2"
          style={{ background: "#0a0d14", border: "0.5px solid #1e4d7a", color: "#a8acbb" }}
        >
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" style={{ color: "#f59e0b" }} />
          <div>
            <div className="font-semibold" style={{ color: "#f0f2f8" }}>{sourceMeta.label} needs a key</div>
            <div className="text-[11px] mt-0.5">{sourceMeta.description}</div>
          </div>
        </div>
      )}

      <div className="rounded-md p-4 space-y-3" style={{ background: "#0a0d14", border: "0.5px solid #1e2130" }}>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] uppercase tracking-wider" style={{ color: "#4a5068" }}>Genre</Label>
            <Select value={genre} onValueChange={setGenre}>
              <SelectTrigger className="h-8 bg-bg border-line text-xs mt-1" data-testid="ai-genre"><SelectValue /></SelectTrigger>
              <SelectContent>{BED_GENRES.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider" style={{ color: "#4a5068" }}>Mood</Label>
            <Select value={mood} onValueChange={setMood}>
              <SelectTrigger className="h-8 bg-bg border-line text-xs mt-1" data-testid="ai-mood"><SelectValue /></SelectTrigger>
              <SelectContent>{MOODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label className="text-[10px] uppercase tracking-wider" style={{ color: "#4a5068" }}>Duration</Label>
          <div className="flex flex-wrap gap-1.5 mt-1" data-testid="ai-duration">
            {DURATION_PRESETS.map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setDuration(d)}
                className="px-3 py-1 rounded-full text-[11px] transition"
                style={duration === d
                  ? { background: "#1a3a5c", color: "#4a9eff", border: "0.5px solid #4a9eff66" }
                  : { background: "#0f1117", color: "#c8ccd8", border: "0.5px solid #1e2130" }
                }
              >
                {d}s
              </button>
            ))}
            <Input
              type="number"
              min={5}
              max={600}
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value) || 60)}
              className="h-7 w-20 bg-bg border-line text-[11px]"
              data-testid="ai-duration-custom"
            />
          </div>
        </div>

        <div>
          <Label className="text-[10px] uppercase tracking-wider" style={{ color: "#4a5068" }}>Description (optional)</Label>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe your bed e.g. 'smooth late night jazz with piano'"
            className="bg-bg border-line text-xs mt-1 resize-none"
            rows={2}
            data-testid="ai-description"
          />
        </div>

        <div>
          <Label className="text-[10px] uppercase tracking-wider" style={{ color: "#4a5068" }}>Intensity</Label>
          <div className="flex gap-1.5 mt-1">
            {["low", "medium", "high"].map((lvl) => (
              <button
                key={lvl}
                type="button"
                onClick={() => setIntensity(lvl)}
                data-testid={`ai-intensity-${lvl}`}
                className="px-3 py-1 rounded-full text-[11px] transition"
                style={intensity === lvl
                  ? { background: "#1a3a5c", color: "#4a9eff", border: "0.5px solid #4a9eff66" }
                  : { background: "#0f1117", color: "#c8ccd8", border: "0.5px solid #1e2130" }
                }
              >
                {lvl}
              </button>
            ))}
          </div>
        </div>

        <button
          type="button"
          onClick={generate}
          disabled={busy || !sourceMeta?.enabled}
          data-testid="ai-generate-btn"
          className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-md text-[13px] font-semibold transition disabled:opacity-50"
          style={{ background: "#1a3a5c", color: "#4a9eff" }}
        >
          {busy
            ? <><Loader2 className="h-4 w-4 animate-spin" /> Generating your {genre} bed…</>
            : <><Sparkles className="h-4 w-4" /> Generate music bed</>
          }
        </button>
        {busy && (
          <div className="flex items-center justify-center gap-1 h-6" data-testid="ai-render-pulse">
            {Array.from({ length: 24 }).map((_, i) => (
              <span
                key={i}
                className="w-0.5 rounded-full animate-pulse"
                style={{
                  height: `${30 + Math.sin(i / 2) * 25}%`,
                  background: "#4a9eff",
                  animationDelay: `${i * 80}ms`,
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
